import { useCallback, useMemo, useState } from 'react';

export interface RowSelection<TEntity> {
  selectedIds: Set<string>;
  selectedRows: TEntity[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  toggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  clear: () => void;
}

/**
 * Selection for the CURRENT page, keyed by row id.
 *
 * `resetKey` is any change that alters what the page is showing — filters,
 * search, sort, page number. A selection that outlived one of those would let a
 * bulk action act on rows the reader can no longer see, which is the classic
 * way a bulk delete goes wrong.
 */
export function useRowSelection<TEntity extends { id: string }>(
  rows: TEntity[],
  resetKey: string
): RowSelection<TEntity> {
  const [ids, setIds] = useState<Set<string>>(new Set());
  const [seenKey, setSeenKey] = useState(resetKey);

  // Reset during render rather than in an effect: an effect would paint one
  // frame with a stale selection before correcting itself.
  if (resetKey !== seenKey) {
    setSeenKey(resetKey);
    setIds(new Set());
  }

  const onPage = useMemo(() => new Set(rows.map((row) => row.id)), [rows]);

  // An id whose row has left the page is not selectable any more, so it must not
  // reach a bulk action either.
  const selectedRows = useMemo(() => rows.filter((row) => ids.has(row.id)), [rows, ids]);
  const selectedIds = useMemo(() => new Set([...ids].filter((id) => onPage.has(id))), [ids, onPage]);

  const toggle = useCallback((id: string) => {
    setIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const toggleAll = useCallback(() => {
    setIds((current) => {
      const everySelected = rows.length > 0 && rows.every((row) => current.has(row.id));
      return everySelected ? new Set() : new Set(rows.map((row) => row.id));
    });
  }, [rows]);

  const clear = useCallback(() => setIds(new Set()), []);

  return {
    selectedIds,
    selectedRows,
    isSelected: (id: string) => selectedIds.has(id),
    toggle,
    toggleAll,
    allSelected,
    someSelected: selectedRows.length > 0,
    clear,
  };
}
